
import { useState, useEffect, useMemo, useCallback, Suspense } from 'react';
import { lazyWithRetry } from '@/utils/lazy-with-retry';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { supabase } from '@/integrations/supabase/client';
import { Sparkles } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LoginForm } from '@/components/auth/LoginForm';
import { SignupForm } from '@/components/auth/SignupForm';
import { ForgotPasswordForm } from '@/components/auth/ForgotPasswordForm';
import { ResetPasswordForm } from '@/components/auth/ResetPasswordForm';
import { useIsMobile } from '@/hooks/use-mobile';
import { useToast } from '@/hooks/use-toast';
import { useRedirectAuthenticated } from '@/hooks/use-redirect-authenticated';

// Lazy load animation to improve LCP
const AuthAnimation = lazyWithRetry(() => import('@/components/auth/AuthAnimation').then(module => ({
  default: module.AuthAnimation
})), 'AuthAnimation');

const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isPasswordReset = searchParams.get('mode') === 'reset';

  // Only redirect authenticated users if not in password reset mode
  useRedirectAuthenticated('/', !isPasswordReset);

  const isMobile = useIsMobile();
  const modeFromUrl = searchParams.get('mode');

  const [authMode, setAuthMode] = useState<'login' | 'signup' | 'forgot' | 'reset'>(() => {
    return modeFromUrl === 'reset' ? 'reset' : 'login';
  });

  // Enhanced auth state change handling with better error handling
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      try {
        switch (event) {
          case 'PASSWORD_RECOVERY':
            setAuthMode('reset');
            toast({
              title: "Password Reset",
              description: "You can now set your new password.",
            });
            break;

          case 'SIGNED_IN':
            if (authMode === 'reset') {
              // Don't redirect immediately during password reset
              return;
            }
            break;

          case 'SIGNED_OUT':
            if (authMode === 'reset') {
              // Reset was successful, redirect to login
              setAuthMode('login');
            }
            break;

          default:
            break;
        }
      } catch (error) {
        console.error('Error handling auth state change:', error);
        toast({
          title: "Authentication Error",
          description: "An error occurred during authentication. Please try again.",
          variant: "destructive",
        });
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [authMode, toast]);

  // Handle URL-based mode changes
  useEffect(() => {
    if (modeFromUrl === 'reset') {
      setAuthMode('reset');
    }
  }, [modeFromUrl]);

  const toggleAuthMode = useCallback(() => {
    setAuthMode(authMode === 'login' ? 'signup' : 'login');
  }, [authMode]);

  const handleForgotPassword = useCallback(() => {
    setAuthMode('forgot');
  }, []);

  const handleBackToLogin = useCallback(() => {
    setAuthMode('login');
  }, []);

  const handleResetSuccess = useCallback(() => {
    toast({
      title: "Success!",
      description: "Your password has been updated successfully.",
    });
    setAuthMode('login');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Memoize title and description to prevent recalculation
  const title = useMemo(() => {
    switch (authMode) {
      case 'signup':
        return 'Create Account';
      case 'forgot':
        return 'Reset Password';
      case 'reset':
        return 'Set New Password';
      default:
        return 'Sign in to Meetlytic';
    }
  }, [authMode]);

  const description = useMemo(() => {
    switch (authMode) {
      case 'signup':
        return 'Start your free trial - transcribe meetings instantly';
      case 'forgot':
        return 'Enter your email to receive a password reset link';
      case 'reset':
        return 'Enter your new password below';
      default:
        return 'Sign in to access your Meetings';
    }
  }, [authMode]);

  // Memoize form component to prevent unnecessary re-renders
  const formComponent = useMemo(() => {
    switch (authMode) {
      case 'signup':
        return <SignupForm />;
      case 'forgot':
        return <ForgotPasswordForm onBackToLogin={handleBackToLogin} />;
      case 'reset':
        return <ResetPasswordForm onSuccess={handleResetSuccess} />;
      default:
        return <LoginForm onForgotPassword={handleForgotPassword} />;
    }
  }, [authMode, handleBackToLogin, handleForgotPassword, handleResetSuccess]);

  // Memoize footer to prevent unnecessary re-renders
  const footerComponent = useMemo(() => {
    if (authMode === 'forgot' || authMode === 'reset') {
      return null;
    }

    return (
      <CardFooter className="flex flex-col bg-white/40 md:backdrop-blur-sm px-6 py-5 border-t border-white/20">
        <div className="text-center w-full">
          <p className="text-sm text-gray-600 mb-3">
            {authMode === 'login' ? "Don't have an account?" : "Already have an account?"}
          </p>
          <Button
            variant="outline"
            onClick={toggleAuthMode}
            className="w-full bg-white hover:bg-gray-50 text-gray-900 border-gray-300 hover:border-gray-400 font-medium"
            size={isMobile ? "lg" : "default"}
          >
            {authMode === 'login' ? 'Create an account' : 'Sign in instead'}
          </Button>
        </div>
      </CardFooter>
    );
  }, [authMode, isMobile, toggleAuthMode]);

  return (
    <AppLayout background="vibrant-grid">
      <div className="py-4 md:py-8 relative min-h-[85vh] flex items-center justify-center">
        <Card className="shadow-xl max-w-md w-full mx-auto relative z-10 overflow-hidden rounded-xl border-0 ring-1 ring-black/5 bg-white/95 backdrop-blur-sm">
          <Suspense fallback={
            <div className="w-full h-44 md:h-52 bg-gradient-to-r from-blue-50 to-sky-50 rounded-t-lg flex items-center justify-center">
              <div className="auth-skeleton skeleton-input w-32 h-6"></div>
            </div>
          }>
            <AuthAnimation />
          </Suspense>

          <CardHeader className="space-y-2 pt-8 bg-white/50 text-center">
            {/* Logo */}
            <div className="flex justify-center mb-2">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/5 border border-primary/10">
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="font-semibold text-primary tracking-tight">Meetlytic</span>
              </div>
            </div>

            <CardTitle className="text-3xl font-bold tracking-tight text-gray-900">
              {title}
            </CardTitle>
            <CardDescription className="text-base text-gray-600 max-w-xs mx-auto">
              {description}
            </CardDescription>
          </CardHeader>

          <CardContent className="px-6 py-4 bg-white/50">
            {formComponent}
          </CardContent>

          {footerComponent}
        </Card>

        {/* Enhanced background elements with modern blur effects - using brand colors */}
        <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-primary/10 rounded-full opacity-50 blur-3xl"></div>
        <div className="absolute top-20 -right-20 w-96 h-96 bg-purple-100 rounded-full opacity-40 blur-3xl"></div>
        <div className="absolute -top-32 left-20 w-64 h-64 bg-indigo-50 rounded-full opacity-30 blur-3xl"></div>
      </div>
    </AppLayout>
  );
};

export default AuthPage;
